import React, { useState } from 'react';
import { Alert } from '@mui/material';
import LateralPanel from '../../../../../muiComponents/LateralPanel';
import { useContractDetail } from '../../../../contextsProperty/ContextContractDetail';
import useTranslate from '../../../../../customHooks/useTranslate';
import IconButton from '../../../../../muiComponents/IconButton';
import Grid from '../../../../../muiComponents/Grid';
import GridChild from '../../../../../muiComponents/GridChild';
import {
    full_ColumnGrid,
    gridSpacing20,
    gridSpacing30,
} from '../../../../../utils/commonVariables/grid';
import TextInput from '../../../../../muiComponents/TextInput/TextInput';
import Button from '../../../../../muiComponents/Button';
import { CANCELLATION_POLICY_VALIDATION_INITIAL_STATE } from '../../../../../utils/contractUtils/contractUtils';
import { validateForm } from '../../../../../utils/validateForms';
import { useNotification } from '../../../../../contexts/NotificationsContext';
import DeletePopUp from '../DeletePopUp';
import { FeedbackMessages } from '../../../../../utils/commonVariables/messages';
import LoadingPanel from './LoadingPanel';
import PanelTitle from '../../../yourProperties/Title';
import useUserProfile from '../../../../../customHooks/useUserProfile';
import ReadOnlyUserAlert from '../../../ReadOnlyUserAlert';

interface CancellationPolicyPanelProps {
    open: boolean;
    onClose: () => void;
}

const CancellationPolicyPanel = (props: CancellationPolicyPanelProps) => {
    const { open, onClose } = props;
    const translate = useTranslate();
    const { setErrorMessage, setSuccessMessage } = useNotification();
    const { readOnlyUser } = useUserProfile();
    const [deletePopUpVisible, setDeletePopUpVisible] = useState(false);
    const {
        isLoadingPanel,
        handleLoadingPanel,
        isBlockedContract,
        cancellationPolicyEdition,
        handleChangeCancellationPolicyEdition,
        handleSaveCancellationPolicy,
    } = useContractDetail();

    const isNewPolicy =
        !cancellationPolicyEdition.id ||
        parseInt(cancellationPolicyEdition.id) <= 0;

    const [validatedForm, setValidatedForm] = useState(false);
    const [formErrors, setFormErrors] = useState(
        CANCELLATION_POLICY_VALIDATION_INITIAL_STATE
    );

    const fieldsToValidate = {
        name: {
            value: cancellationPolicyEdition.name,
            validators: ['requiredString'],
        },
        content: {
            value: cancellationPolicyEdition.content,
            validators: ['requiredString'],
        },
    };

    const validateInputsForm = () => {
        setFormErrors(CANCELLATION_POLICY_VALIDATION_INITIAL_STATE);
        const errors = validateForm(fieldsToValidate);
        if (errors.length) {
            for (const error of errors) {
                setFormErrors(prev => ({
                    ...prev,
                    [error.inputKey]: error.errorMessage,
                }));
            }
        }
        return errors.length === 0;
    };

    return (
        <>
            {deletePopUpVisible && !readOnlyUser && (
                <DeletePopUp
                    open={!!deletePopUpVisible}
                    onSave={async () => {
                        try {
                            handleLoadingPanel(true);
                            await handleSaveCancellationPolicy({
                                ...cancellationPolicyEdition,
                                Deleted: true,
                            });
                            setSuccessMessage(
                                FeedbackMessages.COMMON_SUCCESS_DELETE
                            );
                        } catch (error) {
                            setErrorMessage(FeedbackMessages.COMMON_ERROR);
                        } finally {
                            handleLoadingPanel(false);
                            setDeletePopUpVisible(false);
                            onClose();
                        }
                    }}
                    onCancel={() => {
                        setDeletePopUpVisible(false);
                    }}
                ></DeletePopUp>
            )}
            <LateralPanel open={open}>
                {isLoadingPanel ? (
                    <LoadingPanel />
                ) : (
                    <form className='flex-column height100perc flex-between'>
                        <div className='flex-column  gap-30'>
                            <div className='flex-between flex-align-center contract-panel__title'>
                                <PanelTitle
                                    text={
                                        isNewPolicy
                                            ? 'new_cancellation_policy'
                                            : cancellationPolicyEdition.name
                                    }
                                    className='text-secondary-color'
                                    translated={isNewPolicy}
                                />
                                <IconButton
                                    iconName='Close'
                                    onClick={onClose}
                                />
                            </div>

                            {readOnlyUser ? (
                                <ReadOnlyUserAlert text='read_only_contract' />
                            ) : (
                                isBlockedContract && (
                                    <Alert severity='info'>
                                        {translate(
                                            'bloqued_information_contract'
                                        )}
                                    </Alert>
                                )
                            )}
                            <div className='flex-column  gap-10 '>
                                <Grid
                                    className='border-color-top padding-top-20'
                                    style={{ width: 'auto' }}
                                    columnSpacing={gridSpacing30}
                                    rowSpacing={gridSpacing20}
                                >
                                    <GridChild gridProps={full_ColumnGrid}>
                                        <TextInput
                                            required
                                            id='name'
                                            labelText='name'
                                            value={
                                                cancellationPolicyEdition.name
                                            }
                                            disabled={
                                                isLoadingPanel ||
                                                isBlockedContract ||
                                                readOnlyUser
                                            }
                                            handleChange={(
                                                e: React.ChangeEvent<HTMLInputElement>
                                            ) => {
                                                handleChangeCancellationPolicyEdition(
                                                    {
                                                        name: e.target.value,
                                                        __isChanged: true,
                                                    }
                                                );
                                            }}
                                            onBlur={() => {
                                                if (validatedForm) {
                                                    validateInputsForm();
                                                }
                                            }}
                                            error={!!formErrors.name}
                                            errorMessage={formErrors.name}
                                        />
                                    </GridChild>
                                    <GridChild gridProps={full_ColumnGrid}>
                                        <TextInput
                                            required
                                            id='content'
                                            labelText='content'
                                            value={
                                                cancellationPolicyEdition.content
                                            }
                                            multiline
                                            rows={6}
                                            disabled={
                                                isLoadingPanel ||
                                                isBlockedContract ||
                                                readOnlyUser
                                            }
                                            handleChange={(
                                                e: React.ChangeEvent<HTMLInputElement>
                                            ) =>
                                                handleChangeCancellationPolicyEdition(
                                                    {
                                                        content: e.target.value,
                                                        __isChanged: true,
                                                    }
                                                )
                                            }
                                            onBlur={() => {
                                                if (validatedForm) {
                                                    validateInputsForm();
                                                }
                                            }}
                                            error={!!formErrors.content}
                                            errorMessage={formErrors.content}
                                        />
                                    </GridChild>
                                </Grid>
                            </div>
                        </div>
                        <div
                            className={`flex-${
                                isNewPolicy ? 'end' : 'between'
                            }`}
                        >
                            {!isNewPolicy && (
                                <Button
                                    className='delete-button'
                                    onClick={() => {
                                        setDeletePopUpVisible(true);
                                    }}
                                    disabled={
                                        isLoadingPanel ||
                                        isBlockedContract ||
                                        readOnlyUser
                                    }
                                >
                                    {translate('delete')}
                                </Button>
                            )}

                            <div className='flex-end gap-20'>
                                <Button
                                    variant='outlined'
                                    onClick={onClose}
                                    disabled={isLoadingPanel}
                                >
                                    {translate('cancel')}
                                </Button>
                                <Button
                                    variant='contained'
                                    onClick={async () => {
                                        setValidatedForm(true);
                                        const validated = validateInputsForm();
                                        if (validated) {
                                            await handleSaveCancellationPolicy();
                                        }
                                    }}
                                    disabled={
                                        isLoadingPanel ||
                                        isBlockedContract ||
                                        readOnlyUser
                                    }
                                >
                                    {translate('save')}
                                </Button>
                            </div>
                        </div>
                    </form>
                )}
            </LateralPanel>
        </>
    );
};

export default CancellationPolicyPanel;
